// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.CalculatedField.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import { isEmpty, isNil, find, map, indexOf, remove, isBoolean } from 'lodash/fp';
import { observable, computed, action, toJS } from 'mobx';

import { DATA_TYPES } from 'logic/data/Izenda.ReportDesignerConstants';
import constants from 'logic/data/Izenda.CalculatedFieldConstants';

export default class CalculatedField {
  @observable id = null;
  @observable tempId = null;
  @observable name = '';
  @observable functionName = null;
  @observable expression = '';
  @observable izendaDataType = null;
  @observable isRunningField = false;
  @observable isCompositeField = false;
  @observable format = {};
  @observable suggestedDataType = null;
  @observable previewResult = '';
  @observable querySourceId = null; // Parent Id
  @observable fieldDataType = null;
  @observable type = constants.TYPES.ADD;
  @observable validExpression = false;
  @observable previewRecord = null;
  @observable formats = []; // list format for subtotal and grand total
  @observable dataTypes = constants.DATA_TYPES;
  @observable hasSupportDefaultTotal = true;
  @observable formatDataType = '';
  @observable canSetSubtotal = true;

  @computed
  get functions() {
    return this.loadFunctionsForDataType();
  }
  @computed
  get disablePreview() {
    let disableExpression = false;

    if (this.type.functionLabel) {
      disableExpression =
        (!this.expression && this.functionName === constants.FUNCTION_NAME.EXPRESSION) ||
        this.functionName === constants.FUNCTION_NAME.NONE;
    } else {
      disableExpression = !this.expression;
    }

    return disableExpression || !this.validExpression;
  }

  constructor(data: any) {
    data && this.populateData(data);
  }

  /**
   * Populate data for CalculatedField
   */
  @action
  populateData(data: any) {
    data = data || {};
    this.id = data.id;
    this.tempId = data.tempId;
    this.name = data.name;
    this.functionName = data.functionName;
    this.expression = data.expression;
    this.izendaDataType = data.izendaDataType;
    this.isRunningField = data.isRunningField;
    this.isCompositeField = data.isCompositeField;
    this.format = data.format;
    this.suggestedDataType = data.suggestedDataType;
    this.previewResult = data.previewResult;
    this.querySourceId = data.querySourceId;
    this.fieldDataType = data.fieldDataType;
    this.type = data.type;
    this.validExpression = data.validExpression;
    this.previewRecord = data.previewRecord;
    this.formats = data.formats;
    this.hasSupportDefaultTotal = data.hasSupportDefaultTotal;
    this.formatDataType = data.formatDataType;
    this.canSetSubtotal = data.canSetSubtotal;
  }

  /**
   * Read initial dats
   * @param {any} data
   * @param {string} type
   */
  @action
  readInitialData(data: any, type: string) {
    data = data || {};

    this.name = data.name || '';
    this.functionName = data.functionName || constants.FUNCTION_NAME.NONE;
    this.expression = data.expression || '';
    this.izendaDataType = data.izendaDataType || null;
    this.isRunningField = data.isRunningField || false;
    this.isCompositeField = data.isCompositeField || false;
    this.tempId = data.tempId;
    this.id = data.id || null;
    this.querySourceId = data.querySourceId || '';
    this.fieldDataType = data.fieldDataType || '';
    this.type = type || constants.TYPES.ADD;
    this.validExpression = !!data.expression;
    this.previewRecord = data.previewRecord || null;
    this.format = data.format || {};
    this.hasSupportDefaultTotal = isBoolean(data.hasSupportDefaultTotal) ? data.hasSupportDefaultTotal : true;
    this.formatDataType = data.formatDataType || '';
    this.canSetSubtotal = isBoolean(data.canSetSubtotal) ? data.canSetSubtotal : true;

    return Promise.resolve();
  }

  clone() {
    const data = toJS(this);
    const cloned = new CalculatedField();
    cloned.readInitialData(data, data.type);
    return cloned;
  }

  /**
   * Change field name
   * @param {string} value
   */
  @action
  changeFunctionName(value: string) {
    this.functionName = value;
    this.loadSuggestedDataTypeByFunction();

    // Reset model when change function to NONE
    if (value === constants.FUNCTION_NAME.NONE) {
      this.resetData();
    }
  }

  /**
   * Reset model when change function to NONE
   */
  @action
  resetData() {
    this.name = '';
    this.expression = '';
    this.izendaDataType = null;
    this.isRunningField = false;
    this.isCompositeField = false;
    this.format = {};
    this.previewResult = '';
  }

  /**
   * Change function name
   * @param {string} value
   */
  changeFieldName(value: string) {
    this.name = value;
  }

  /**
   * Change expression
   * @param {string} value
   */
  changeExpression(value: string) {
    this.expression = value;
  }

  /**
   * Change expression
   * @param {string} value
   */
  changeDataType(value: string) {
    this.izendaDataType = value;
  }

  /**
   * Change format type
   * @param {String} value
   */
  changeFormat(value: string) {
    this.format = value;
  }

  /**
   * Read suggestion data type when validate expression successfully
   * @param {any} result
   */
  @action
  readDataType(result: any) {
    const resultData = result.result || {};

    if (resultData.izendaDataType) {
      this.izendaDataType = resultData.izendaDataType;
      this.isRunningField = resultData.isRunningField;
      this.isCompositeField = resultData.isCompositeField;
    }
    this.validExpression = isEmpty(result.messages);
    return result.messages;
  }

  /**
   * Read preview result
   * @param {any} result
   */
  @action
  readPreviewResult(result: any) {
    if (result.messages) return result.messages;

    this.previewResult = !isNil(result.value) ? result.value : '';
    this.izendaDataType = result.dataType;
    return null;
  }

  /**
   * Load data type by its value
   * @param {string} dataType
   */
  loadDataTypeByValue(dataType: string) {
    return find(item => item.value === dataType, constants.DATA_TYPES);
  }

  /**
   * Load supported functions of a data type
   */
  loadFunctionsForDataType() {
    const dataType = this.getFinalDataType();
    const matchedDataType = this.loadDataTypeByValue(dataType);

    const functions = matchedDataType
      ? map(item => find({ value: item }, constants.FUNCTIONS), matchedDataType.functions)
      : [];
    return remove({ value: constants.FUNCTION_NAME.DEFAULT }, functions);
  }

  /**
   * Load suggested data type for function
   */
  loadSuggestedDataTypeByFunction() {
    this.izendaDataType = this.calculateSuggestedDataTypeByFunction();
  }

  /**
   * Get final data type if the field apply aggregated function
   */
  getFinalDataType() {
    return isEmpty(this.formatDataType) ? this.fieldDataType : this.formatDataType;
  }

  /**
   * Compute suggested data type for function
   */
  calculateSuggestedDataTypeByFunction() {
    const dataType = isEmpty(this.formatDataType)
      ? this.fieldDataType
      : this.formatDataType;
    switch (this.functionName) {
      case constants.FUNCTION_NAME.NONE:
      case constants.FUNCTION_NAME.EXPRESSION:
      case constants.FUNCTION_NAME.DEFAULT:
        return null;
      case constants.FUNCTION_NAME.COUNT:
      case constants.FUNCTION_NAME.COUNT_DISTINCT:
        return DATA_TYPES.NUMERIC;
      case constants.FUNCTION_NAME.AVERAGE:
      case constants.FUNCTION_NAME.SUM:
      case constants.FUNCTION_NAME.SUM_DISTINCT:
      default:
        return dataType;
    }
  }
  /**
   * Check function support data type
   */
  doesFunctionAcceptDataType() {
    const suggestedDataType = this.calculateSuggestedDataTypeByFunction();
    const suggestedDataTypeDetail = this.loadDataTypeByValue(suggestedDataType);

    return (
      suggestedDataType === this.izendaDataType ||
      indexOf(this.izendaDataType, suggestedDataTypeDetail.toDataTypes) !== -1
    );
  }
}



// WEBPACK FOOTER //
// ./ui/logic/models/Izenda.CalculatedField.js